import { debounce } from '@mui/material/utils';
import { UIEvent, useCallback, useEffect, useMemo } from 'react';

const DEFAULT_THRESHOLD_PX = 400;
const DEFAULT_FETCH_MORE_LIMIT = 50;
const CHECK_INTERVAL_MS = 200;

type OffsetBasedLoadMore = (localLimit?: number, localOffset?: number) => Promise<void> | null;

type ScrollTarget = UIEvent | HTMLElement;

type PaginateHandler = ((e: ScrollTarget) => Promise<void>) & { clear?: () => void };

/*
  Like debounce, but also runs on the leading edge: the first call goes through
  immediately and later ones are spaced by intervalMs. A trailing-only debounce never
  runs while the user keeps scrolling — it waits for them to stop — so pagination can
  only ever trigger where the scroll ENDS, which in a reversed thread means the very
  top edge.
*/
function throttleLeading(fn: PaginateHandler, intervalMs: number): PaginateHandler {
  let lastRunAt = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pending: ScrollTarget | null = null;

  const run = (target: ScrollTarget): Promise<void> => {
    lastRunAt = Date.now();
    return fn(target);
  };

  const throttled: PaginateHandler = (target: ScrollTarget) => {
    const elapsed = Date.now() - lastRunAt;
    if (elapsed >= intervalMs) {
      return run(target);
    }

    pending = target;
    if (!timer) {
      timer = setTimeout(() => {
        timer = null;
        const next = pending;
        pending = null;
        if (next) {
          void run(next);
        }
      }, intervalMs - elapsed);
    }
    return Promise.resolve();
  };

  throttled.clear = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    pending = null;
  };

  return throttled;
}

export const useScrollPaginateHandler = (params: {
  loadMore?: OffsetBasedLoadMore;
  enabled?: boolean;
  thresholdPx?: number;
  /*
    Extra threshold as a percentage of the scroller's VISIBLE height, added on top of
    thresholdPx. A pixel-only threshold means how early we fetch depends on the pane's
    height, so a tall pane still has to be scrolled to its very edge. Percent of the
    visible height rather than of the content, so the distance stays predictable as the
    thread grows.
  */
  thresholdPercentOfHeight?: number;
  /*
    Check the threshold during the scroll instead of only after it settles. Needed for
    prefetching ahead of the reader; opt-in so existing callers keep their behavior.
  */
  checkWhileScrolling?: boolean;
  limit?: number;
  // It will be used to check top scroll instead of bottom scroll
  reversed?: boolean;
}) => {
  const {
    loadMore,
    enabled = false,
    thresholdPx = DEFAULT_THRESHOLD_PX,
    thresholdPercentOfHeight = 0,
    checkWhileScrolling = false,
    limit = DEFAULT_FETCH_MORE_LIMIT,
    reversed = false,
  } = params;

  const rawHandler = useCallback(
    // an element is accepted directly for callers that need to re-check the
    // threshold outside a scroll event (e.g. a wheel at the very top, which
    // cannot move scrollTop and so never produces one)
    async (e: ScrollTarget) => {
      if (!enabled) return;

      const el = e instanceof HTMLElement ? e : (e.target as HTMLElement);
      const scrollTop = el.scrollTop;
      const clientHeight = el.clientHeight;
      const scrollHeight = el.scrollHeight;

      const distanceToBottom = scrollHeight - (scrollTop + clientHeight);
      /*
        A reversed list is laid out with column-reverse, where scrollTop is 0 at the
        visual bottom and -(scrollHeight - clientHeight) at the visual top — so the
        plain scrollTop of a normal list would read as "at the top" everywhere.
      */
      const distanceToTop = reversed ? scrollHeight - clientHeight + scrollTop : scrollTop;

      const threshold = thresholdPx + (clientHeight * thresholdPercentOfHeight) / 100;
      const isBottom = distanceToBottom <= threshold;
      const isTop = distanceToTop <= threshold;

      if (!reversed && isBottom) {
        await loadMore?.(limit);
      } else if (reversed && isTop) {
        await loadMore?.(limit);
      }
    },
    [enabled, thresholdPx, thresholdPercentOfHeight, reversed, loadMore, limit],
  );

  const scheduledOnScroll = useMemo(() => {
    return checkWhileScrolling
      ? throttleLeading(rawHandler, CHECK_INTERVAL_MS)
      : debounce(rawHandler, CHECK_INTERVAL_MS);
  }, [rawHandler, checkWhileScrolling]);

  useEffect(() => {
    return () => {
      scheduledOnScroll.clear?.();
    };
  }, [scheduledOnScroll]);

  return scheduledOnScroll;
};
